import * as React from 'react';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import IconButton from '@mui/material/IconButton';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import { useNavigate, useSearchParams } from 'react-router';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  getMany as getOffers,
  deleteOne as deleteOffer,
  type Offer,
} from '../data/offers';
import { getAllCountries, type Country } from '../data/countries';
import PageContainer from './PageContainer';
import {useLanguage} from "../../contexts/LanguageContext";
import {useAuth} from "../../contexts/AuthContext";

// Skeleton Card Component for Loading State
function SkeletonCard() {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 1,
        backgroundColor: 'transparent',
      }}
    >
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          borderRadius: '8px 8px 0 0',
          height: 200,
          width: '100%',
        }}
      />
      <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 2 }}>
        <Skeleton variant="text" width="80%" height={32} animation="wave" sx={{ mb: 1, mx: 'auto' }} />
        <Skeleton variant="rectangular" width={60} height={24} animation="wave" sx={{ mb: 2, mx: 'auto', borderRadius: 2 }} />
        <Skeleton variant="text" width="100%" height={20} animation="wave" sx={{ mb: 1 }} />
        <Skeleton variant="text" width="90%" height={20} animation="wave" sx={{ mb: 2 }} />
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="text" width="60%" height={24} animation="wave" sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={8} animation="wave" sx={{ borderRadius: 4 }} />
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <Skeleton variant="circular" width={32} height={32} animation="wave" />
        <Skeleton variant="circular" width={32} height={32} animation="wave" />
      </CardActions>
    </Card>
  );
}

// Image Gallery Slider Component
function ImageGallerySlider({ images, status, isRtl }: { images: string[]; status?: 'DRAFT' | 'PUBLISHED'; isRtl: boolean }) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isHovered, setIsHovered] = React.useState(false);

  const handlePrevious = (event: React.MouseEvent) => {
    event.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (event: React.MouseEvent) => {
    event.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (!images || images.length === 0) {
    return (
      <CardMedia
        component="div"
        sx={{
          height: '100%',
          width: '100%',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No images
        </Typography>
      </CardMedia>
    );
  }

  return (
    <Box
      sx={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardMedia
        component="img"
        image={images[currentIndex]}
        alt={`Image ${currentIndex + 1}`}
        sx={{
          objectFit: 'cover',
          borderRadius: '8px 8px 0 0',
          width: '100%',
          height: '100%',
        }}
      />
      {/* Status Chip Overlay */}
      {/*{status === 'DRAFT' && (
        <Chip
          label={status}
          size="small"
          color="default"
          sx={{
            position: 'absolute',
            top: 8,
            ...(isRtl ? { right: 8 } : { left: 8 }),
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 600,
          }}
        />
      )}*/}
      {images.length > 1 && (
        <>
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              opacity: isHovered ? 0.6 : 0,
              transition: 'opacity 0.3s ease',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              opacity: isHovered ? 0.6 : 0,
              transition: 'opacity 0.3s ease',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            <Typography variant="caption">
              {currentIndex + 1} / {images.length}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

export default function OfferList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();

  const dialogs = useDialogs();
  const notifications = useNotification();

  const [offers, setOffers] = React.useState<Offer[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [totalCount, setTotalCount] = React.useState(0);
  const [countries, setCountries] = React.useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = React.useState(true);

  const currentPage = React.useMemo(() => {
    return parseInt(searchParams.get('page') || '1', 10);
  }, [searchParams]);

  const countryFilter = React.useMemo(() => {
    return searchParams.get('country_id') || '';
  }, [searchParams]);

  const perPage = 12; // Cards per page
  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const { user } = useAuth();
  React.useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countriesData = await getAllCountries();
        setCountries(countriesData);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const loadData = React.useCallback(async () => {
    setIsLoading(true);

    try {
      const params: any = {
        page: currentPage,
        per_page: perPage,
      };

      if (countryFilter) {
        params.country_id = countryFilter;
      }

      const result = await getOffers(params);

      result.data.forEach((offer) => {
        offer.gallery_image_urls = offer.gallery_image_urls?.length > 0 ? offer.gallery_image_urls : ["https://ichef.bbci.co.uk/ace/standard/976/cpsprodpb/14235/production/_100058428_mediaitem100058424.jpg", "https://i0.wp.com/picjumbo.com/wp-content/uploads/beautiful-fall-nature-scenery-free-image.jpeg?w=2210&quality=70", "https://thumbs.dreamstime.com/b/young-conceptual-image-large-stone-shape-human-brain-conceptual-image-large-stone-shape-110748113.jpg"];
      });

      setOffers(result.data);
      setTotalCount(result.total);
    } catch (error) {
      notifications.show(`Failed to load offers. Reason: ${(error as Error).message}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
    }

    setIsLoading(false);
  }, [currentPage, countryFilter, notifications]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = React.useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      const newParams: any = { page: page.toString() };
      if (countryFilter) {
        newParams.country_id = countryFilter;
      }
      setSearchParams(newParams);
    },
    [setSearchParams, countryFilter],
  );

  const handleCountryFilterChange = React.useCallback(
    (event: SelectChangeEvent) => {
      const newParams: any = { page: '1' };
      if (event.target.value) {
        newParams.country_id = event.target.value;
      }
      setSearchParams(newParams);
    },
    [setSearchParams],
  );

  const handleCreateClick = React.useCallback(() => {
    navigate('/offers/new');
  }, [navigate]);

  const handleCardClick = React.useCallback(
    (id: number) => () => {
      navigate(`/offers/${id}`);
    },
    [navigate],
  );



  const handleDeleteClick = React.useCallback(
    (offer: Offer) => async () => {
      const confirmed = await dialogs.confirm(
        t('offers.deletePopup.deleteOfferMessage'),
        {
          title: t('offers.deletePopup.deleteOffer'),
          severity: 'error',
          okText: t('offers.deletePopup.confirm'),
          cancelText: t('offers.deletePopup.cancel'),
        },
      );

      if (confirmed) {
        try {
          await deleteOffer(offer.id);
          notifications.show(t('messages.deleteSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });
          loadData();
        } catch (error) {
          notifications.show(
            `Failed to delete offer. Reason: ${(error as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      }
    },
    [dialogs, notifications, loadData, t],
  );

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <PageContainer
      breadcrumbs={[{ title: t('offers.title') }]}
      actions={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200, '& .MuiInputBase-root': { marginTop: 0 } }}>
            <Select
              value={countryFilter}
              onChange={handleCountryFilterChange}
              disabled={loadingCountries}
              displayEmpty
              sx={{
                backgroundColor: 'transparent',
                height: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  top: 0
                }
              }}
            >
              <MenuItem value="">
                <em>{t('common.selectCountry')}</em>
              </MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id.toString()}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            {user?.role === 'GM' || user?.role === 'BM' ? (
          <RtlButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{
              background: '#028bcb !important',
              boxShadow: 'unset !important',
              border: '0 !important',
              '&:hover': { border: '0 !important', background: '#0277a8 !important' }
            }}
          >
              {t('common.create')}
          </RtlButton>
                ) : null}
        </Box>
      }
    >
      <Box sx={{ width: '100%' }}>
        {isLoading ? (
          <Grid container spacing={3}>
            {Array.from({ length: perPage }).map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                <SkeletonCard />
              </Grid>
            ))}
          </Grid>
        ) : offers.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography>No offers found</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {offers.map((offer) => {
                const isDisabled = offer.seats_total === 0;
                const seatsColor = offer.seats_total > 10 ? '#4caf50' : '#f44336';
                const isAdmin = user?.role === 'GM' || user?.role === 'BM';
                const canClickDisabled = isDisabled && !isAdmin;

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={offer.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: canClickDisabled ? 'not-allowed' : 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s',
                        opacity: isDisabled ? 0.5 : 1,
                        filter: isDisabled ? 'grayscale(70%)' : 'none',
                        position: 'relative',
                        '&:hover': {
                          transform: canClickDisabled ? 'none' : 'translateY(-4px)',
                          boxShadow: canClickDisabled ? 1 : 4,
                        },
                        boxShadow: 1,
                        backgroundColor: 'transparent',
                        '&::before': isDisabled ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          pointerEvents: 'none',
                          zIndex: 1,
                          borderRadius: 'inherit',
                        } : {},
                      }}
                      onClick={canClickDisabled ? undefined : handleCardClick(offer.id)}
                    >
                      {/* Image Section */}
                      <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
                        <ImageGallerySlider
                          images={offer.gallery_image_urls || []}
                          status={offer.status}
                          isRtl={isRtl}
                        />
                        {isDisabled && (
                          <Chip
                            label={t('offers.soldOut') || 'Sold Out'}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              ...(isRtl ? { left: 8 } : { right: 8 }),
                              backgroundColor: 'rgba(244, 67, 54, 0.95)',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              zIndex: 2,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            }}
                          />
                        )}
                      </Box>

                      {/* Content Section */}
                      <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 2 }}>
                        {/* Title */}
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto',
                          }}
                        >
                          {offer.title}
                        </Typography>

                        {/* Divider */}
                        <Divider sx={{ my: 1 }} />

                        {/* Country Name */}
                        {offer.country && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {offer.country.name}
                          </Typography>
                        )}

                        {/* Seats Number */}
                        {offer.seats_total !== undefined && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              mt: 2,
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              backgroundColor: isDisabled ? 'rgba(0, 0, 0, 0.05)' : `${seatsColor}15`,
                              border: `1px solid ${isDisabled ? 'rgba(0, 0, 0, 0.1)' : seatsColor}`,
                            }}
                          >
                            <EventSeatIcon
                              sx={{
                                fontSize: '1.2rem',
                                color: isDisabled ? 'text.disabled' : seatsColor,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: isDisabled ? 'text.disabled' : seatsColor,
                              }}
                            >
                              {offer.seats_total} {t('offers.availableSeats')}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>

                      {/* Action Buttons */}
                      { (user?.role === 'GM' || user?.role === 'BM')  && (
                        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/offers/${offer.id}/edit`);
                            }}
                            aria-label="edit"
                            color="primary"
                            sx={{
                              '&:hover': {
                                backgroundColor: '#31394a',
                                color: 'white'
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(offer)();
                            }}
                            aria-label="delete"
                            color="error"
                            sx={{
                              '&:hover': {
                                background: 'red',
                                color: 'white'
                              },
                              marginRight: `${isRtl ? 8 : 8}px`,
                              marginLeft: `${isRtl ? 0 : 8}px`
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </CardActions>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </PageContainer>
  );
}

